import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import {
  ExclamationCircleIcon,
  ArrowPathIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import CategoryForm from './forms/CategoryForm';

interface Category {
  id: number;
  name: string;
}

const CategoryManager: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Delete state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiClient.get('/api/categories/');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setIsEditMode(false);
    setIsFormOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setIsEditMode(true);
    setIsFormOpen(true);
  };

  const handleDeleteCategory = (category: Category) => {
    setCategoryToDelete(category);
    setIsDeleteConfirmOpen(true);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await apiClient.delete(`/api/categories/${categoryToDelete.id}/`);

      if (response.status >= 200 && response.status < 300) {
        setCategories(categories.filter(cat => cat.id !== categoryToDelete.id));
        setIsDeleteConfirmOpen(false);
        setCategoryToDelete(null);
      } else {
        throw new Error(`Failed to delete category (${response.status})`);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      setDeleteError('Failed to delete category. It may be in use by other items.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCategoryCreated = (newCategory: Category) => {
    setCategories([...categories, newCategory]);
    setIsFormOpen(false);
  };

  const handleCategoryUpdated = (updatedCategory: Category) => {
    setCategories(categories.map(cat =>
      cat.id === updatedCategory.id ? updatedCategory : cat
    ));
    setIsFormOpen(false);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingCategory(null);
    setIsEditMode(false);
  };

  const handleDeleteCancel = () => {
    setIsDeleteConfirmOpen(false);
    setCategoryToDelete(null);
    setDeleteError(null);
  };

  // Filter categories based on search query
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
      }}>
        <ArrowPathIcon style={{
          width: '32px',
          height: '32px',
          color: COLORS.text.tertiary,
          animation: 'spin 1s linear infinite',
        }} />
        <span style={{
          marginLeft: SPACING[8],
          color: COLORS.text.secondary,
          fontSize: TYPOGRAPHY.body.sizes.default,
        }}>Loading categories...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#FEF2F2',
        border: `1px solid #FECACA`,
        borderRadius: RADIUS,
        padding: SPACING[16],
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ExclamationCircleIcon style={{
            height: '20px',
            width: '20px',
            color: COLORS.status.error,
            marginRight: SPACING[8],
          }} />
          <div>
            <span style={{ color: '#B91C1C', fontSize: TYPOGRAPHY.body.sizes.default }}>{error}</span>
            <button
              onClick={fetchCategories}
              style={{
                marginLeft: SPACING[16],
                color: COLORS.status.error,
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: TYPOGRAPHY.body.sizes.default,
              }}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          <TagIcon style={{ height: '24px', width: '24px', color: COLORS.text.tertiary }} />
          <h2 style={{
            fontSize: TYPOGRAPHY.size['2xl'],
            fontWeight: TYPOGRAPHY.weight.semibold,
            color: COLORS.text.primary,
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            margin: 0,
          }}>Categories</h2>
          <span style={{
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.text.secondary,
          }}>({categories.length})</span>
        </div>
        <button
          onClick={handleAddCategory}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: `${SPACING[8]} ${SPACING[16]}`,
            border: 'none',
            fontSize: TYPOGRAPHY.body.sizes.default,
            fontWeight: TYPOGRAPHY.weight.medium,
            borderRadius: RADIUS,
            color: '#FFFFFF',
            backgroundColor: COLORS.accent.default,
            cursor: 'pointer',
          }}
        >
          <PlusIcon style={{ height: '16px', width: '16px', marginRight: SPACING[8] }} />
          Add Category
        </button>
      </div>

      {/* Search */}
      <div style={{ maxWidth: '28rem' }}>
        <label htmlFor="search" style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          borderWidth: 0,
        }}>
          Search categories
        </label>
        <input
          type="text"
          id="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            display: 'block',
            width: '100%',
            borderRadius: RADIUS,
            border: `1px solid ${COLORS.border.default}`,
            padding: `${SPACING[8]} ${SPACING[12]}`,
            fontSize: TYPOGRAPHY.body.sizes.default,
            fontFamily: TYPOGRAPHY.body.fontFamily,
            outline: 'none',
            boxSizing: 'border-box',
          }}
          placeholder="Search categories..."
        />
      </div>

      {/* Categories List */}
      <div style={{
        backgroundColor: COLORS.bg.panel,
        borderRadius: RADIUS,
        border: `1px solid ${COLORS.border.default}`,
      }}>
        {filteredCategories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: `${SPACING[48]} ${SPACING[16]}` }}>
            <TagIcon style={{
              margin: '0 auto',
              height: '48px',
              width: '48px',
              color: COLORS.text.tertiary,
              display: 'block',
            }} />
            <h3 style={{
              marginTop: SPACING[8],
              fontSize: TYPOGRAPHY.body.sizes.default,
              fontWeight: TYPOGRAPHY.weight.medium,
              color: COLORS.text.primary,
            }}>
              {searchQuery ? 'No categories found' : 'No categories'}
            </h3>
            <p style={{
              marginTop: SPACING[4],
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.text.secondary,
            }}>
              {searchQuery ? 'Try adjusting your search terms.' : 'Get started by creating a new category.'}
            </p>
            {!searchQuery && (
              <div style={{ marginTop: SPACING[24] }}>
                <button
                  onClick={handleAddCategory}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: `${SPACING[8]} ${SPACING[16]}`,
                    border: 'none',
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    borderRadius: RADIUS,
                    color: '#FFFFFF',
                    backgroundColor: COLORS.accent.default,
                    cursor: 'pointer',
                  }}
                >
                  <PlusIcon style={{ height: '16px', width: '16px', marginRight: SPACING[8] }} />
                  Add Category
                </button>
              </div>
            )}
          </div>
        ) : (
          <div>
            {filteredCategories.map((category, index) => (
              <div
                key={category.id}
                style={{
                  padding: SPACING[16],
                  borderTop: index > 0 ? `1px solid ${COLORS.border.default}` : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
                    <div style={{ flexShrink: 0 }}>
                      <TagIcon style={{ height: '20px', width: '20px', color: COLORS.text.tertiary }} />
                    </div>
                    <div>
                      <h3 style={{
                        fontSize: TYPOGRAPHY.body.sizes.default,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        color: COLORS.text.primary,
                        margin: 0,
                      }}>{category.name}</h3>
                      <p style={{
                        fontSize: TYPOGRAPHY.body.sizes.default,
                        color: COLORS.text.secondary,
                        margin: 0,
                      }}>Category ID: {category.id}</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                    <button
                      onClick={() => handleEditCategory(category)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: SPACING[4],
                        border: 'none',
                        borderRadius: '50%',
                        color: COLORS.text.tertiary,
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                      }}
                    >
                      <PencilIcon style={{ height: '16px', width: '16px' }} />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: SPACING[4],
                        border: 'none',
                        borderRadius: '50%',
                        color: COLORS.status.error,
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                      }}
                    >
                      <TrashIcon style={{ height: '16px', width: '16px' }} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Category Form Modal */}
      <CategoryForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onCategoryCreated={handleCategoryCreated}
        onCategoryUpdated={handleCategoryUpdated}
        initialCategory={editingCategory}
        isEditMode={isEditMode}
      />

      {/* Delete Confirmation Modal */}
      {isDeleteConfirmOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 50,
          overflowY: 'auto',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            minHeight: '100vh',
            paddingTop: SPACING[16],
            paddingLeft: SPACING[16],
            paddingRight: SPACING[16],
            paddingBottom: '80px',
            textAlign: 'center',
          }}>
            <div style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(107, 114, 128, 0.75)',
              transition: 'opacity 0.3s',
            }} />

            <span style={{
              display: 'inline-block',
              verticalAlign: 'middle',
              height: '100vh',
            }} aria-hidden="true">
              &#8203;
            </span>

            <div style={{
              display: 'inline-block',
              verticalAlign: 'bottom',
              backgroundColor: COLORS.bg.panel,
              borderRadius: RADIUS,
              paddingLeft: SPACING[16],
              paddingRight: SPACING[16],
              paddingTop: '20px',
              paddingBottom: SPACING[16],
              textAlign: 'left',
              overflow: 'hidden',
              transform: 'translateY(0)',
              transition: 'all 0.3s',
              marginTop: SPACING[32],
              marginBottom: SPACING[32],
              maxWidth: '32rem',
              width: '100%',
              padding: SPACING[24],
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{
                  margin: '0 auto',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '48px',
                  width: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#FEE2E2',
                  marginLeft: 0,
                  marginRight: 0,
                }}>
                  <ExclamationCircleIcon style={{ height: '24px', width: '24px', color: '#DC2626' }} />
                </div>
                <div style={{
                  marginTop: SPACING[12],
                  textAlign: 'center',
                  marginLeft: SPACING[16],
                  flex: 1,
                }}>
                  <h3 style={{
                    fontSize: TYPOGRAPHY.size.lg,
                    lineHeight: '24px',
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                    margin: 0,
                  }}>
                    Delete Category
                  </h3>
                  <div style={{ marginTop: SPACING[8] }}>
                    <p style={{
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      color: COLORS.text.secondary,
                      margin: 0,
                    }}>
                      Are you sure you want to delete the category "{categoryToDelete?.name}"?
                      This action cannot be undone, and any items using this category will lose their categorization.
                    </p>
                  </div>
                  {deleteError && (
                    <div style={{
                      marginTop: SPACING[16],
                      borderRadius: RADIUS,
                      backgroundColor: '#FEF2F2',
                      padding: SPACING[16],
                    }}>
                      <div style={{ display: 'flex' }}>
                        <ExclamationCircleIcon style={{ height: '20px', width: '20px', color: COLORS.status.error }} />
                        <div style={{ marginLeft: SPACING[12] }}>
                          <p style={{
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            color: '#B91C1C',
                            margin: 0,
                          }}>{deleteError}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div style={{
                marginTop: '20px',
                display: 'flex',
                flexDirection: 'row-reverse',
                gap: SPACING[12],
              }}>
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  style={{
                    display: 'inline-flex',
                    justifyContent: 'center',
                    borderRadius: RADIUS,
                    border: 'none',
                    padding: `${SPACING[8]} ${SPACING[16]}`,
                    backgroundColor: COLORS.status.error,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: '#FFFFFF',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    opacity: isDeleting ? 0.5 : 1,
                  }}
                >
                  {isDeleting ? (
                    <>
                      <ArrowPathIcon style={{
                        width: '16px',
                        height: '16px',
                        marginRight: SPACING[8],
                        animation: 'spin 1s linear infinite',
                      }} />
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  disabled={isDeleting}
                  style={{
                    display: 'inline-flex',
                    justifyContent: 'center',
                    borderRadius: RADIUS,
                    border: `1px solid ${COLORS.border.default}`,
                    padding: `${SPACING[8]} ${SPACING[16]}`,
                    backgroundColor: COLORS.bg.panel,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    opacity: isDeleting ? 0.5 : 1,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManager;
